import { DocumentRenderer } from '@keystone-6/document-renderer';
import type { GetServerSidePropsContext, NextPage } from 'next';
import Image from 'next/image';
import Script from 'next/script';
import { Helmet } from 'react-helmet';
import CarouselWrapper from '../components/carousel-wrapper/carousel-wrapper.component';
import ContactForm from '../components/contact-form/contact-form.component';
import Container from '../components/container/container.component';
import SectionTitle from '../components/section-title/section-title.component';
import { getHomepage } from '../lib';
import styles from '../styles/homepage.module.scss';
import PageTemplate from '../templates/page.template';
import { Homepage } from '../types/homepage.interface';
import { MappedOptions } from '../types/mapped-options-and-menus.interface';
import { Menu } from '../types/menu.interface';
import { PostListItem } from '../types/post-list-item.interface';

interface Props {
  menu: Menu;
  options: MappedOptions;
  homepage: Homepage;
  title: string;
  posts: PostListItem[];
}

const Home: NextPage<Props> = ({ menu, options, homepage, title, posts }) => {
  return (
    <PageTemplate
      menu={menu}
      options={options}
      title={title}
      description={options.description}
      isInitiallyDark={true}
    >
      <Helmet>
        <script async src="https://platform.twitter.com/widgets.js"></script>
      </Helmet>
      <header
        id="start"
        className={styles.landing}
        style={{
          backgroundImage: `url(${homepage.landingImage.image.url})`,
          backgroundPosition: 'center',
        }}
      >
        <Container first={true}>
          <div className={styles.img}>
            <Image
              src={`http://localhost:5000${options.brandingImage}`}
              alt={homepage.landingTitle}
              width="636"
              height="275"
            />
          </div>
        </Container>
      </header>
      <section id="about">
        <Container column={true}>
          <div className={styles.introWrapper}>
            <DocumentRenderer document={homepage.intro.document} />
          </div>
        </Container>
      </section>

      <section>
        <Container>
          <Script src="https://cdn.lightwidget.com/widgets/lightwidget.js" strategy="afterInteractive" />

          <iframe
            src="https://cdn.lightwidget.com/widgets/1e2dc400acba5d9d84e42c70061d14e9.html"
            scrolling="no"
            allowTransparency={true} // eslint-disable-line react/no-unknown-property
            className="lightwidget-widget"
            style={{ width: '100%', border: 0, overflow: 'hidden' }}
          ></iframe>
        </Container>
      </section>

      <section id="db" className={styles.content}>
        <Container column={true}>
          <div className={styles.databaseIntro}>
            <DocumentRenderer document={homepage.callToAction.document} />
          </div>
        </Container>
      </section>

      <section id="contact">
        <Container>
          <div className={styles.contactWrapper}>
            <SectionTitle title="Contact" />
            <p>{homepage.contact}</p>
            <ContactForm />
          </div>
        </Container>
      </section>
      <section>
        <Container column={true} css={{ padding: '100px 0', height: 'auto' }}>
          <div>
            <CarouselWrapper
              images={homepage.sponsors}
              title="Institutions involved and sponsors"
              baseUrl={options.baseUrl}
            />
          </div>
        </Container>
      </section>
    </PageTemplate>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const homepage = await getHomepage();

  ctx.res.setHeader('Cache-Control', 'public, max-age=300, s-maxage=300');

  return {
    props: { ...homepage, title: 'Start' },
  };
}

export default Home;
